import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { LuFileText, LuUsers } from "react-icons/lu";
import { PageHeader } from "@/components/ui/page-header";
import type { ConsoleAuditEntry } from "@/lib/api";

export function AuditPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["console", "audit", "list"],
    queryFn: () => api.audit.list(200),
  });
  const heatmap = useQuery({
    queryKey: ["console", "audit", "heatmap"],
    queryFn: () => api.auditHeatmap(30),
  });

  const columns: Column<ConsoleAuditEntry>[] = [
    {
      header: "When",
      cell: (e) => new Date(e.created_at).toLocaleString(),
      className: "text-[var(--color-text-muted)] whitespace-nowrap",
    },
    { header: "Operator", cell: (e) => e.email },
    { header: "Action", cell: (e) => <code className="text-xs">{e.action}</code> },
    {
      header: "Target",
      cell: (e) =>
        e.target_kind ? (
          <span className="text-xs text-[var(--color-text-muted)]">
            {e.target_kind}: {e.target_id?.slice(0, 12)}…
          </span>
        ) : (
          "—"
        ),
    },
    { header: "IP", cell: (e) => e.ip ?? "—", className: "text-xs text-[var(--color-text-muted)]" },
  ];

  return (
    <>
      <PageHeader
        icon={LuFileText}
        title="Audit log"
        description="Every state-changing console action gets recorded. Read-only and append-only."
      />
      <div className="p-8 space-y-6">
        {heatmap.data && heatmap.data.totals.length > 0 && (
          <Card>
            <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
              <LuUsers className="w-4 h-4 text-[var(--color-text-subtle)]" />
              <p className="text-sm font-semibold text-[var(--color-text)]">
                Operator activity (last {heatmap.data.days}d)
              </p>
            </div>
            <div className="p-5 space-y-4">
              {heatmap.data.totals.map((t) => {
                const max = Math.max(...heatmap.data!.buckets.filter((b) => b.email === t.email).map((b) => b.n), 1);
                const days = Array.from({ length: heatmap.data!.days }).map((_, i) => {
                  const d = new Date();
                  d.setDate(d.getDate() - (heatmap.data!.days - 1 - i));
                  return d.toISOString().split("T")[0];
                });
                return (
                  <div key={t.email}>
                    <div className="flex items-center justify-between mb-1.5 text-xs">
                      <span className="text-[var(--color-text)]">{t.email}</span>
                      <span className="text-[var(--color-text-subtle)] tabular-nums">
                        {t.n.toLocaleString()} actions
                      </span>
                    </div>
                    <div className="flex gap-0.5">
                      {days.map((day) => {
                        const b = heatmap.data!.buckets.find((x) => x.email === t.email && x.day === day);
                        const intensity = b ? Math.min(1, b.n / max) : 0;
                        return (
                          <div
                            key={day}
                            title={`${day}: ${b?.n ?? 0}`}
                            className="h-5 flex-1 rounded-sm"
                            style={{
                              backgroundColor:
                                intensity > 0
                                  ? `rgba(139, 92, 246, ${0.15 + intensity * 0.65})`
                                  : "var(--color-elevated)",
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <Card>
          <DataTable
            columns={columns}
            rows={data?.entries}
            isLoading={isLoading}
            isError={isError}
            rowKey={(e) => e.id}
            emptyMessage="No actions logged yet."
          />
        </Card>
      </div>
    </>
  );
}
